import { cloneDeep, uniq } from "lodash";

import type { ListTableColumnSortBy } from "@/components/elements";
import type { Car, CarColumns, CarPayload } from "@/types/car";
import type { PaginatedList } from "@/types/entity";
import type { CarConditions, CarStatuses, QueryParams } from "./types";

export const CAR_STATUSES: CarStatuses = ["owned", "sold", "totaled", "traded"];
export const CAR_CONDITIONS: CarConditions = ["excellent", "fair", "good", "superb"];

export function mapCarPayloadToCar(car: CarPayload): Car {
  return {
    created_at: car.created_at,
    identifier: car.identifier,
    user: car.user,
    updated_at: car.updated_at,
    ...car.data,
  };
}

function sortDataBy(id: CarColumns, sortBy: ListTableColumnSortBy = "asc") {
  return (a: Car, b: Car) => {
    const aValue = a[id];
    const bValue = b[id];

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return sortBy === "asc" ? -1 : 1;
    if (bValue == null) return sortBy === "asc" ? 1 : -1;

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortBy === "asc" ? aValue - bValue : bValue - aValue;
    }

    return sortBy === "asc"
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  };
}

export function getDataFilteredAndSorted(response: PaginatedList<Car>, params: QueryParams) {
  let data = cloneDeep(response!);
  const { parameters, page, pageSize, sort } = params;
  let results = [...data.results];

  // Client-side filtering
  if (parameters?.condition) {
    results = results.filter(
      ({ condition }) =>
        condition?.toLocaleLowerCase() === parameters.condition?.toLocaleLowerCase()
    );
  }
  if (parameters?.status) {
    results = results.filter(
      ({ status }) => status?.toLocaleLowerCase() === parameters.status?.toLocaleLowerCase()
    );
  }
  if (parameters?.full_text) {
    results = results.filter(({ make, model, nickname }) => {
      const tokens = parameters.full_text!.toLowerCase().split(" ").filter(Boolean);
      return tokens.every(
        (query) =>
          (make || "").toLowerCase().includes(query) ||
          (model || "").toLowerCase().includes(query) ||
          (nickname || "").toLowerCase().includes(query)
      );
    });
  }

  // Client-side sorting
  if (sort) {
    const sorter = sortDataBy(sort.id, sort.sortBy);
    results = results.sort(sorter);
  }

  // Client-side pagination
  const startIndex = ((page || 1) - 1) * (pageSize || 25);
  const endIndex = startIndex + (pageSize || 25);
  data.total = results.length;
  data.results = results.slice(startIndex, endIndex);
  data.page = page || 1;
  data.page_size = pageSize || 25;

  return data;
}

export function getConditionsFromCars(cars?: Car[]): CarConditions {
  if (cars && cars.length > 0) {
    return uniq(cars.map((car) => car.condition))
      .sort()
      .filter(Boolean);
  }

  return CAR_CONDITIONS;
}
export function getStatusesFromCars(cars?: Car[]): CarStatuses {
  if (cars && cars.length > 0) {
    return uniq(cars.map((car) => car.status))
      .sort()
      .filter(Boolean);
  }

  return CAR_STATUSES;
}
